/* eslint-disable react/jsx-handler-names */
import { useRef, useState } from "react";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import { useLocation } from "react-router-dom";

// Constants
import { CATEGORIES_ENUMS } from "constants/navigation/categories";

// Hooks
import useTranslate from "hooks/useTranslate";
import { useBreakpoint } from "hooks/useBreakpoint";
import useClickOutside from "hooks/useClickOutside";

// Icons
import PlayIcon from "assets/icons/play.svg?react";

// Images
import CoverGlory from "assets/images/glory/cover_game.webp";
import CoverBanger from "assets/images/banger/cover-banger.webp";

// Types
import { GameType } from "types/Games";
import { GET_PARAMS } from "constants/popupConfig";
import Skeleton from "react-loading-skeleton";
import useQueryParam from "hooks/useQueryParam";
import Astronaut from "assets/test/astronaut-100hp-gaming-0196c48a-df88-784f-adb1-8c5569b3d104-5.svg?raw";

import "./styles.sass";

type GameProps = {
  category?: CATEGORIES_ENUMS | null;
  game: GameType;
  onInitDemo: () => void;
  onInitByAlias: () => void;
};

const GameCard = (props: GameProps) => {
  const { category, game, onInitDemo, onInitByAlias } = props;
  const providersQuery = useQueryParam(GET_PARAMS.PROVIDERS);

  const { isDesktop } = useBreakpoint();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const providers = query.get(GET_PARAMS.PROVIDERS);
  const [showName, setShowName] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isControlsShow, setIsControlsShow] = useState(false);

  const { VITE_APP_PROJECT_NAME } = import.meta.env;
  const coverGame =
    VITE_APP_PROJECT_NAME === "glory" ? CoverGlory : CoverBanger;

  const cardRef = useRef(null);

  const [effect, api] = useSpring(() => ({
    opacity: 0,
  }));
  const isAstronaut =
    game.urlAlias ===
    "astronaut-100hp-gaming-0196c48a-df88-784f-adb1-8c5569b3d104-5";
  const t = useTranslate();

  const handleRealInit = (e) => {
    e.preventDefault();
    onInitByAlias();
  };

  const handleDemoInit = (e) => {
    e.preventDefault();
    onInitDemo();
  };

  const handleControlShow = () => {
    api.start({ opacity: 1 });
    setIsControlsShow(true);
  };

  const handleControlHide = () => {
    api.start({ opacity: 0 });
    setTimeout(() => {
      setIsControlsShow(false);
    }, 200);
  };

  useClickOutside({
    refs: [cardRef],
    handler: handleControlHide,
    isDisable: isDesktop,
  });

  const coverPath = (): string => {
    const providerName = game.provider.name.toLowerCase();
    const isGlory = VITE_APP_PROJECT_NAME === "glory";
    const isBanger = VITE_APP_PROJECT_NAME === "banger";
    if (providerName === "inout") {
      if (isGlory) {
        return `https://diswdgcu9cfva.cloudfront.net/game-covers/glory/${game.urlAlias}.webp`;
      }
      if (isBanger) {
        return `https://diswdgcu9cfva.cloudfront.net/game-covers/banger/${game.urlAlias}.webp`;
      }
    }

    return `https://diswdgcu9cfva.cloudfront.net/covers2/${game.urlAlias}.webp`;
  };

  const realBtnClass = classNames("GameCard__play_btn", "GameCard__play_real");
  const demoBtnClass = classNames("GameCard__play_btn", "GameCard__play_demo");

  return showName &&
    category === CATEGORIES_ENUMS.POPULAR &&
    providersQuery !== "Banger Games" &&
    providersQuery !== "Glory Games" ? null : (
    <div
      className="GameCard"
      onMouseEnter={isDesktop && !isControlsShow ? handleControlShow : () => {}}
      onMouseLeave={isDesktop && isControlsShow ? handleControlHide : () => {}}
      ref={cardRef}
    >
      <div className="GameCard__cover" onClick={handleControlShow}>
        {isAstronaut && (
          <div
            dangerouslySetInnerHTML={{ __html: Astronaut }}
            style={{ width: "100%", height: "100%" }}
          />
        )}
        {!isLoaded && !isAstronaut && (
          <div className="GameCard__loading">
            <Skeleton width="100%" height="100%" borderRadius={0} />
          </div>
        )}
        {!isAstronaut && (
          <img
            src={coverPath()}
            alt="game"
            loading="lazy"
            className="GameCard__img"
            onLoad={() => {
              setIsLoaded(true);
            }}
            onError={({ currentTarget }) => {
              setShowName(true);
              // eslint-disable-next-line no-param-reassign
              currentTarget.onerror = null; // prevents looping
              // eslint-disable-next-line no-param-reassign
              currentTarget.src = coverGame;
            }}
          />
        )}

        {showName && (
          <div className="GameCard__name">
            <span>{game.customName ? game.customName : game.name}</span>
          </div>
        )}
      </div>

      {isControlsShow && (
        <animated.div
          className="GameCard__play"
          style={{ ...effect }}
          onClick={handleControlHide}
        >
          <button
            type="button"
            className={realBtnClass}
            onClick={handleRealInit}
          >
            {t("profile.game.button")} <PlayIcon />
          </button>
          {game.provider.hasDemo && (
            <button
              type="button"
              className={demoBtnClass}
              onClick={handleDemoInit}
            >
              {t("casino.lobby.demo")}
            </button>
          )}
        </animated.div>
      )}
    </div>
  );
};

export default GameCard;
